import dayjs from 'dayjs'
import type { CurrentUserDateFormat } from '../api/types/user.interfaces'
import { getFileNameAndExtension } from './getFileNameAndExtension'

/** Dayjs format strings for date-only display: 1 Jan, 2026 | Jan 1, 2026 | 2026, Jan 1 */
const USER_DATE_FORMAT_MAP: Record<CurrentUserDateFormat, string> = {
	'dd/MM/yyyy': 'D MMM, YYYY',   // 1 Jan, 2026
	'MM/dd/yyyy': 'MMM D, YYYY',   // Jan 1, 2026
	'yyyy/MM/dd': 'YYYY, MMM D',   // 2026, Jan 1
}

/** Dayjs format strings for date + time */
const USER_DATETIME_FORMAT_MAP: Record<CurrentUserDateFormat, string> = {
	'dd/MM/yyyy': 'D MMM, YYYY, h:mm A',
	'MM/dd/yyyy': 'MMM D, YYYY, h:mm A',
	'yyyy/MM/dd': 'YYYY, MMM D, h:mm A',
}

export const getDateFormatPattern = (userFormat: CurrentUserDateFormat) =>
	USER_DATE_FORMAT_MAP[userFormat]
export const getDateTimeFormatPattern = (userFormat: CurrentUserDateFormat) =>
	USER_DATETIME_FORMAT_MAP[userFormat]

export const formatDateByPreference = (
	date: string | Date,
	userFormat: CurrentUserDateFormat
) => dayjs(date).format(USER_DATE_FORMAT_MAP[userFormat])

export const formatDateTimeByPreference = (
	date: string | Date,
	userFormat: CurrentUserDateFormat
) => dayjs(date).format(USER_DATETIME_FORMAT_MAP[userFormat])

// 0: 'Pending',
// 1: 'Classifying',
// 2: 'Extracting metadata',
// 3: 'Classification file failed',
// 4: 'Extraction metadata failed',
// 5: 'Completed',

export const processingStatuses: Record<number, string> = {
	0: 'Pending',
	1: 'Completed',
	2: 'Processing Document',
	3: 'Processing Document Failed',
}

// 0: 'Pending',
// 1: 'Classifying',
// 2: 'Extracting',
// 3: 'Failed',
// 4: 'Failed',
// 5: 'Completed',

export const processingStatusesShort: Record<number, string> = {
	0: 'Pending',
	1: 'Completed',
	2: 'ProcessingDocument',
	3: 'ProcessingDocumentFailed',
}

export const IMG_TYPES = ['png', 'jpeg', 'jpg', 'gif', 'webp']
export const TEXT_TYPES = ['txt']
export const RENDERING_TYPES = [
	'pdf',
	'docx',
	'doc',
	'xls',
	'xlsx',
	'ppt',
	'pptx',
]

export const FORBIDDEN_EXTENSIONS = [
	'.html',
	'.htm',
	'.php',
	'.php3',
	'.php4',
	'.php5',
	'.phtml',
	'.asp',
	'.aspx',
	'.jsp',
	'.jspx',
	'.pl',
	'.py',
	'.rb',
	'.pyc',
	'.js',
	'.vbs',
	'.bat',
	'.cmd',
	'.sh',
	'.bash',
	'.ps1',
	'.psm1',
	'.exe',
	'.com',
	'.dll',
	'.msi',
	'.app',
	'.deb',
	'.rpm',
	'.pkg',
	'.class',
	'.jar',
	'.war',
	'.ear',
	'.lnk',
	'.url',
	'.reg',
	'.inf',
	'.sys',
	'.drv',
]

/** Format date for display. Pass userFormat from useUserDateFormat() to use user preference. */
export const formatDocumentDate = (
	isoDate: string | Date,
	userFormat?: CurrentUserDateFormat
) =>
	dayjs(isoDate).format(
		userFormat ? USER_DATE_FORMAT_MAP[userFormat] : 'D MMM, YYYY'
	)
/** Format date + time for display. Pass userFormat from useUserDateFormat() to use user preference. */
export const formatDocumentDateTime = (
	isoDate: string | Date,
	userFormat?: CurrentUserDateFormat
) =>
	dayjs(isoDate).format(
		userFormat ? USER_DATETIME_FORMAT_MAP[userFormat] : 'D MMM YYYY, hh:mm A'
	)

export const formatDocumentSize = (
	bytes: number | null,
	unit?: 'KB' | 'MB' | 'GB'
) => {
	if (bytes === null) return '-'
	const KB = 1024
	const MB = KB * 1024
	const GB = MB * 1024

	const format = (value: number) =>
		value % 1 === 0 ? `${value.toFixed(0)}` : `${value.toFixed(2)}`

	if (!unit) {
		if (bytes < KB) return `${bytes} B`
		if (bytes < MB) return `${format(bytes / KB)} KB`
		if (bytes < GB) return `${format(bytes / MB)} MB`
	}
	return `${format(bytes / GB)} ${unit ?? 'GB'}`
}

// export const formatDocumentSize = (bytes: number | null) => {
// 	if (bytes === null) return '-'
// 	if (bytes < 1_000_000) return `${Math.floor((bytes / 1_000) * 100) / 100}KB`
// 	if (bytes < 1_000_000_000)
// 		return `${Math.floor((bytes / 1_000_000) * 100) / 100}MB`
// 	return `${Math.floor((bytes / 1_000_000_000) * 100) / 100}GB`
// }

const getFileType = (mimeType: string) => {
	const map: Record<string, string> = {
		'application/pdf': 'PDF',
		'application/msword': 'DOC',
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
			'DOC',
	}
	return map[mimeType] || 'Document'
}

export const getShortenPath = (pathArray: string[], maxLength?: number) => {
	const MAX_PATH_LENGTH = 50

	const fullPath = pathArray.join('/')

	if (fullPath.length <= (maxLength ?? MAX_PATH_LENGTH)) {
		return fullPath
	}

	const firstFolders = pathArray.slice(0, 2)
	const lastFolder = pathArray[pathArray.length - 1]

	return `${firstFolders.join('/')}/.../${lastFolder}`
}

export const shortenFileName = (
	fileName: string,
	maxCutLength = 20,
	withoutExt = false
) => {
	const parts = fileName.split('.')
	if (parts.length < 2) return fileName

	const ext = parts.pop()
	const name = parts.join('.')

	if (name.length <= maxCutLength) {
		return withoutExt ? name : `${name}.${ext}`
	}

	const start = name.slice(0, Math.ceil(maxCutLength / 2)).trimEnd()
	const end = name.slice(-Math.floor(maxCutLength / 2)).trimStart()

	if (withoutExt) {
		return `${start}...${end}`
	} else {
		return `${start}...${end}.${ext}`
	}
}

export const shortenFolderName = (folderName: string, maxCutLength = 20) => {
	if (folderName?.length <= maxCutLength) {
		return folderName
	}

	const start = folderName.slice(0, Math.ceil(maxCutLength / 2)).trimEnd()
	const end = folderName.slice(-Math.floor(maxCutLength / 2)).trimStart()

	return `${start}...${end}`
}

export const getFileNameAndExt = (
	fileName?: string
): { name: string; ext?: string } => {
	if (!fileName) {
		return {
			name: '',
			ext: '',
		}
	}

	const withoutSuffix = fileName.replace(/\s*\(\d+\)$/, '')

	const parts = withoutSuffix.split('.')
	if (parts.length < 2)
		return {
			name: fileName,
		}

	const ext = parts.pop()
	const name = parts.join('.')

	return {
		name,
		ext,
	}
}

export const documentFormatter = (doc: any) => {
	const isoDate = doc?.deletedAt || doc?.createdAt
	const base = {
		...doc,
		dateTime: formatDocumentDateTime(isoDate),
		createdAt: isoDate,
		date: formatDocumentDate(isoDate),
		createdBy: doc?.deletedBy || doc?.createdBy,
	}

	if (doc?.type === 1) {
		return {
			...base,
			docType: 'folder',
			modifiedAt: doc?.modifiedAt
				? formatDocumentDateTime(doc?.modifiedAt)
				: '',
		}
	}

	if (doc?.type === 0) {
		const extension = getFileNameAndExtension(doc?.name).extension
		return {
			...base,
			fileType: extension.toUpperCase(),
			docType: 'file',
			fileSize: formatDocumentSize(Number(doc?.size)),
			category: doc?.metadata?.category || 'Unclassified',
			subcategory: doc?.metadata?.subcategory || 'Unclassified',
			basic_metadata: doc?.metadata?.basicMetadata || {},
			document_metadata: doc?.metadata?.documentMetadata || {},
			checked: false,
			extension: `.${extension}`,
			modifiedDate: doc?.modifiedAt
				? formatDocumentDateTime(doc?.modifiedAt)
				: '',
			version: `${doc?.version || 1}.0`,
			modifiedBy: doc?.modifiedBy,
		}
	}

	return doc
}
